import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBars,
  FaTimes,
  FaArrowRight,
  FaCheckCircle,
  FaUserMd,
  FaHospital,
  FaBed,
  FaFileMedical,
  FaUsers,
  FaRupeeSign,
  FaCalendarCheck,
  FaClock,
  FaShieldAlt,
  FaMobileAlt,
  FaHeartbeat,
  FaNotesMedical,
  FaFlask,
  FaPills,
  FaChartLine,
  FaPhoneAlt,
  FaWhatsapp,
  FaEnvelope,
  FaLinkedinIn,
  FaStethoscope,
  FaUserNurse,
} from "react-icons/fa";

import { MdHealthAndSafety, MdOutlineEmergency } from "react-icons/md";
import { HiOutlineDocumentReport } from "react-icons/hi";
import { BsArrowUpRight } from "react-icons/bs";

import logo from "../images/logo.png";
import "./home.css";

const platformFeatures = [
  {
    icon: <FaCalendarCheck />,
    title: "OPD Management",
    description:
      "Appointments, doctor schedules, patient queues and consultations in one connected workflow.",
    className: "feature-blue",
  },
  {
    icon: <FaBed />,
    title: "IPD Management",
    description:
      "Manage admissions, rooms, clinical orders, medicines, investigations, charges and discharge.",
    className: "feature-purple",
  },
  {
    icon: <FaHospital />,
    title: "Hospital Information System",
    description:
      "Bring clinical and administrative hospital workflows together on a unified platform.",
    className: "feature-cyan",
  },
  {
    icon: <FaUsers />,
    title: "EMS",
    description:
      "Manage hospital employees, schedules, attendance, leave and workforce operations.",
    className: "feature-orange",
  },
  {
    icon: <FaRupeeSign />,
    title: "Finance & Billing",
    description:
      "Connected billing, patient charges, payments and financial workflows.",
    className: "feature-green",
  },
  {
    icon: <FaUserMd />,
    title: "Doctor Management",
    description:
      "Manage doctors, departments, availability, consultation schedules and patient flow.",
    className: "feature-indigo",
  },
  {
    icon: <FaMobileAlt />,
    title: "Patient Experience",
    description:
      "Help patients discover doctors, book appointments and follow their appointment status.",
    className: "feature-pink",
  },
  {
    icon: <MdHealthAndSafety />,
    title: "ABDM Connected",
    description:
      "Support connected digital-health journeys through ABDM-enabled healthcare workflows.",
    className: "feature-teal",
  },
];

const ipdSteps = [
  { icon: <FaHospital />, label: "Admission" },
  { icon: <FaBed />, label: "Room / Bed" },
  { icon: <FaUserMd />, label: "Doctor Assigned" },
  { icon: <FaNotesMedical />, label: "Clinical Orders" },
  { icon: <FaPills />, label: "Medicines" },
  { icon: <FaFlask />, label: "Investigations" },
  { icon: <FaRupeeSign />, label: "Billing" },
  { icon: <FaFileMedical />, label: "Discharge" },
];

function Home() {
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  const scrollTo = (id) => {
    closeMenu();

    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };

  return (
    <div className="health-website">

      {/* =========================================================
          NAVBAR
      ========================================================= */}

      <header className="main-navbar">
        <div className="nav-container">

          <button
            className="brand"
            onClick={() => scrollTo("home")}
            aria-label="The Health App Home"
          >
            <img src={logo} alt="The Health App" />
            <span>The Health App</span>
          </button>

          <nav className="desktop-nav">
            <button onClick={() => scrollTo("home")}>Home</button>
            <button onClick={() => scrollTo("platform")}>Platform</button>
            <button onClick={() => scrollTo("patients")}>For Patients</button>
            <button onClick={() => scrollTo("hospitals")}>For Hospitals</button>
            <button onClick={() => scrollTo("abdm")}>ABDM</button>
            <button onClick={() => scrollTo("contact")}>Contact</button>
          </nav>

          <a
            className="nav-book-button"
            href="https://play.google.com/store/apps/details?id=com.health.health_us&pcampaignid=web_share"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaCalendarCheck />
            Book Appointment
          </a>

          <button
            className="mobile-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Open navigation"
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        <div className={`mobile-navigation ${menuOpen ? "open" : ""}`}>
          <button onClick={() => scrollTo("home")}>Home</button>
          <button onClick={() => scrollTo("platform")}>Platform</button>
          <button onClick={() => scrollTo("patients")}>For Patients</button>
          <button onClick={() => scrollTo("hospitals")}>For Hospitals</button>
          <button onClick={() => scrollTo("abdm")}>ABDM</button>
          <button onClick={() => scrollTo("contact")}>Contact</button>

          <a
            href="https://play.google.com/store/apps/details?id=com.health.health_us&pcampaignid=web_share"
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMenu}
          >
            Book Appointment
          </a>
        </div>
      </header>

      {/* =========================================================
          HERO
      ========================================================= */}

      <main>

        <section className="hero-section" id="home">

          <div className="hero-orb hero-orb-one" />
          <div className="hero-orb hero-orb-two" />

          <div className="page-container hero-grid">

            <div className="hero-copy">

              <div className="eyebrow">
                <span className="eyebrow-dot" />
                Connected Healthcare Platform
              </div>

              <h1>
                One Connected Platform for
                <span> Modern Healthcare.</span>
              </h1>

              <p className="hero-description">
                Connect patients, doctors and hospital operations through one
                intelligent healthcare ecosystem — from appointments and OPD
                to IPD, workforce, billing and digital health.
              </p>

              <div className="hero-actions">

                <a
                  href="https://play.google.com/store/apps/details?id=com.health.health_us&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="primary-button"
                >
                  Book Appointment
                  <FaArrowRight />
                </a>

                <button
                  className="secondary-button"
                  onClick={() => scrollTo("hospitals")}
                >
                  Explore for Hospitals
                  <FaArrowRight />
                </button>

              </div>

              <div className="hero-trust-row">

                <div>
                  <span className="trust-icon">
                    <FaClock />
                  </span>
                  <p>
                    <strong>Real-time</strong>
                    Appointment updates
                  </p>
                </div>

                <div>
                  <span className="trust-icon">
                    <FaShieldAlt />
                  </span>
                  <p>
                    <strong>Connected</strong>
                    Healthcare workflows
                  </p>
                </div>

                <div>
                  <span className="trust-icon">
                    <FaHospital />
                  </span>
                  <p>
                    <strong>One Platform</strong>
                    Hospital operations
                  </p>
                </div>

              </div>
            </div>

            {/* ===================================================
                CUSTOM HERO PRODUCT ILLUSTRATION
                No external stock image required.
            =================================================== */}

            <div className="hero-visual">

              <div className="hero-decoration hero-decoration-one" />
              <div className="hero-decoration hero-decoration-two" />

              <div className="dashboard-window">

                <div className="dashboard-topbar">

                  <div className="mini-brand">
                    <img src={logo} alt="" />
                    <span>The Health App</span>
                  </div>

                  <div className="dashboard-search">
                    Search patients, doctors...
                  </div>

                  <div className="avatar">
                    <FaUserMd />
                  </div>

                </div>

                <div className="dashboard-body">

                  <aside className="dashboard-sidebar">

                    <div className="sidebar-logo">
                      <FaHeartbeat />
                    </div>

                    <div className="side-item active">
                      <FaChartLine />
                    </div>

                    <div className="side-item">
                      <FaCalendarCheck />
                    </div>

                    <div className="side-item">
                      <FaBed />
                    </div>

                    <div className="side-item">
                      <FaUserMd />
                    </div>

                    <div className="side-item">
                      <FaRupeeSign />
                    </div>

                  </aside>

                  <div className="dashboard-content">

                    <div className="dashboard-heading">
                      <div>
                        <small>Good morning,</small>
                        <h3>Hospital Team</h3>
                      </div>

                      <span className="live-badge">
                        <span />
                        Live
                      </span>
                    </div>

                    <div className="metric-grid">

                      <div className="metric-card">
                        <span className="metric-icon green">
                          <FaStethoscope />
                        </span>
                        <div>
                          <small>OPD</small>
                          <strong>Patient Flow</strong>
                        </div>
                      </div>

                      <div className="metric-card">
                        <span className="metric-icon purple">
                          <FaBed />
                        </span>
                        <div>
                          <small>IPD</small>
                          <strong>Admissions</strong>
                        </div>
                      </div>

                      <div className="metric-card">
                        <span className="metric-icon blue">
                          <FaCalendarCheck />
                        </span>
                        <div>
                          <small>Today</small>
                          <strong>Appointments</strong>
                        </div>
                      </div>

                    </div>

                    <div className="dashboard-lower">

                      <div className="chart-card">

                        <div className="chart-header">
                          <div>
                            <small>Patient Flow</small>
                            <strong>Live Overview</strong>
                          </div>

                          <span>This week</span>
                        </div>

                        <div className="fake-chart">
                          <div className="chart-line">
                            <span className="chart-point p1" />
                            <span className="chart-point p2" />
                            <span className="chart-point p3" />
                            <span className="chart-point p4" />
                            <span className="chart-point p5" />
                          </div>

                          <div className="chart-grid-lines">
                            <span />
                            <span />
                            <span />
                            <span />
                          </div>
                        </div>

                      </div>

                      <div className="appointment-card">

                        <div className="appointment-title">
                          <strong>Patient Queue</strong>
                          <span>Live</span>
                        </div>

                        <div className="appointment-row">
                          <div className="patient-avatar">A</div>
                          <div>
                            <strong>Patient</strong>
                            <small>General Medicine</small>
                          </div>
                          <span className="queue-chip">In Queue</span>
                        </div>

                        <div className="appointment-row">
                          <div className="patient-avatar second">R</div>
                          <div>
                            <strong>Patient</strong>
                            <small>Cardiology</small>
                          </div>
                          <span className="next-chip">Next</span>
                        </div>

                        <div className="appointment-row">
                          <div className="patient-avatar third">S</div>
                          <div>
                            <strong>Patient</strong>
                            <small>Orthopaedics</small>
                          </div>
                          <span className="scheduled-chip">Booked</span>
                        </div>

                      </div>

                    </div>

                  </div>

                </div>

              </div>

              {/* Patient phone illustration */}

              <div className="phone-mockup">

                <div className="phone-speaker" />

                <div className="phone-screen">

                  <div className="phone-brand">
                    <img src={logo} alt="" />
                    <span>Health App</span>
                  </div>

                  <p>Hello 👋</p>
                  <h4>Your healthcare, connected.</h4>

                  <div className="phone-search">
                    Find a doctor
                  </div>

                  <div className="phone-actions">

                    <div>
                      <span>
                        <FaCalendarCheck />
                      </span>
                      Book
                    </div>

                    <div>
                      <span>
                        <FaClock />
                      </span>
                      Queue
                    </div>

                  </div>

                  <div className="phone-queue-card">

                    <small>Appointment Status</small>

                    <div className="phone-queue-status">
                      <span className="pulse-dot" />
                      <strong>In Queue</strong>
                    </div>

                    <p>Follow your appointment status in real time.</p>

                  </div>

                </div>

              </div>

              <div className="floating-status floating-status-one">
                <span>
                  <FaCheckCircle />
                </span>
                <div>
                  <small>Connected</small>
                  <strong>Patient Journey</strong>
                </div>
              </div>

              <div className="floating-status floating-status-two">
                <span>
                  <FaHeartbeat />
                </span>
                <div>
                  <small>Hospital</small>
                  <strong>Operations Live</strong>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* =========================================================
            PLATFORM
        ========================================================= */}

        <section className="platform-section section-space" id="platform">

          <div className="page-container">

            <div className="section-heading centered">

              <div className="section-tag">
                <MdHealthAndSafety />
                THE PLATFORM
              </div>

              <h2>
                Everything your healthcare ecosystem
                <span> needs to stay connected.</span>
              </h2>

              <p>
                The Health App brings patient-facing experiences and hospital
                operations together through connected modules built around
                real healthcare workflows.
              </p>

            </div>

            <div className="feature-grid">

              {platformFeatures.map((feature, index) => (
                <article
                  className="platform-card"
                  key={`${feature.title}-${index}`}
                >
                  <div className={`platform-icon ${feature.className}`}>
                    {feature.icon}
                  </div>

                  <h3>{feature.title}</h3>

                  <p>{feature.description}</p>

                  <div className="card-arrow">
                    <BsArrowUpRight />
                  </div>
                </article>
              ))}

            </div>

          </div>
        </section>

        {/* =========================================================
            PATIENT SECTION
        ========================================================= */}

        <section className="patient-section section-space" id="patients">

          <div className="page-container patient-grid">

            <div className="patient-copy">

              <div className="section-tag patient-tag">
                <FaMobileAlt />
                FOR PATIENTS
              </div>

              <h2>
                Book smarter.
                <br />
                <span>Wait less.</span>
              </h2>

              <p className="section-description">
                Make the appointment journey easier by helping patients find
                doctors, book consultations and follow their appointment
                status without unnecessary uncertainty.
              </p>

              <div className="check-list">

                <div>
                  <FaCheckCircle />
                  <span>Find and book doctors online</span>
                </div>

                <div>
                  <FaCheckCircle />
                  <span>Follow queue position and appointment status</span>
                </div>

                <div>
                  <FaCheckCircle />
                  <span>Know when your consultation is approaching</span>
                </div>

                <div>
                  <FaCheckCircle />
                  <span>Keep healthcare interactions connected</span>
                </div>

              </div>

              <a
                href="https://play.google.com/store/apps/details?id=com.health.health_us&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
              >
                Book Appointment
              </a>

            </div>

            <div className="patient-experience">

              <div className="patient-bg-circle circle-one" />
              <div className="patient-bg-circle circle-two" />

              <div className="patient-phone-large">

                <div className="patient-phone-header">
                  <img src={logo} alt="" />
                  <div>
                    <small>The Health App</small>
                    <strong>My Appointment</strong>
                  </div>
                </div>

                <div className="doctor-mini-card">

                  <div className="doctor-avatar">
                    <FaUserMd />
                  </div>

                  <div>
                    <strong>Your Doctor</strong>
                    <small>Consultation</small>
                  </div>

                  <span>Confirmed</span>

                </div>

                <div className="queue-panel">

                  <div className="queue-label">
                    <span>LIVE QUEUE</span>
                    <span className="live-dot" />
                  </div>

                  <strong className="queue-number">3</strong>
                  <p>patients ahead of you</p>

                  <div className="queue-progress">
                    <span />
                  </div>

                  <div className="queue-time">
                    <FaClock />
                    Appointment progress updates
                  </div>

                </div>

              </div>

              <div className="patient-floating-card first">

                <span className="patient-floating-icon">
                  <FaCalendarCheck />
                </span>

                <div>
                  <small>Appointment</small>
                  <strong>Confirmed</strong>
                </div>

                <FaCheckCircle className="success-icon" />

              </div>

              <div className="patient-floating-card second">

                <span className="patient-floating-icon blue">
                  <FaClock />
                </span>

                <div>
                  <small>Queue Status</small>
                  <strong>Your turn is approaching</strong>
                </div>

              </div>

            </div>

          </div>
        </section>

        {/* =========================================================
            HOSPITAL SECTION
        ========================================================= */}

        <section className="hospital-section section-space" id="hospitals">

          <div className="page-container">

            <div className="hospital-layout">

              <div className="hospital-visual">

                <div className="hospital-dashboard">

                  <div className="hospital-dashboard-header">

                    <div>
                      <span className="hospital-small-logo">
                        <FaHospital />
                      </span>

                      <div>
                        <small>THE HEALTH APP</small>
                        <strong>Hospital Operations</strong>
                      </div>
                    </div>

                    <span className="system-online">
                      <i />
                      Connected
                    </span>

                  </div>

                  <div className="hospital-module-grid">

                    <div className="hospital-module">
                      <span className="blue">
                        <FaStethoscope />
                      </span>
                      <div>
                        <small>OUTPATIENT</small>
                        <strong>OPD</strong>
                      </div>
                    </div>

                    <div className="hospital-module">
                      <span className="purple">
                        <FaBed />
                      </span>
                      <div>
                        <small>INPATIENT</small>
                        <strong>IPD</strong>
                      </div>
                    </div>

                    <div className="hospital-module">
                      <span className="green">
                        <FaUserNurse />
                      </span>
                      <div>
                        <small>WORKFORCE</small>
                        <strong>EMS</strong>
                      </div>
                    </div>

                    <div className="hospital-module">
                      <span className="orange">
                        <FaRupeeSign />
                      </span>
                      <div>
                        <small>OPERATIONS</small>
                        <strong>Finance</strong>
                      </div>
                    </div>

                  </div>

                  <div className="hospital-overview">

                    <div className="overview-title">
                      <div>
                        <small>Unified Operations</small>
                        <strong>Connected workflows</strong>
                      </div>

                      <HiOutlineDocumentReport />
                    </div>

                    <div className="overview-bars">

                      <div>
                        <span>OPD workflow</span>
                        <i>
                          <b style={{ width: "86%" }} />
                        </i>
                      </div>

                      <div>
                        <span>IPD workflow</span>
                        <i>
                          <b style={{ width: "73%" }} />
                        </i>
                      </div>

                      <div>
                        <span>Hospital operations</span>
                        <i>
                          <b style={{ width: "92%" }} />
                        </i>
                      </div>

                    </div>

                  </div>

                </div>

                <div className="hospital-floating-info">

                  <div className="hospital-floating-icon">
                    <FaChartLine />
                  </div>

                  <div>
                    <small>ONE ECOSYSTEM</small>
                    <strong>Connected hospital operations</strong>
                  </div>

                </div>

              </div>

              <div className="hospital-copy">

                <div className="section-tag hospital-tag">
                  <FaHospital />
                  FOR HOSPITALS
                </div>

                <h2>
                  One system.
                  <br />
                  <span>Across the hospital.</span>
                </h2>

                <p className="section-description">
                  Connect clinical, administrative, workforce and financial
                  workflows instead of managing hospital operations in
                  isolation.
                </p>

                <div className="hospital-benefits">

                  <div>
                    <span>
                      <FaStethoscope />
                    </span>

                    <div>
                      <strong>Clinical workflows</strong>
                      <p>
                        OPD and IPD journeys designed around doctors and
                        patients.
                      </p>
                    </div>
                  </div>

                  <div>
                    <span>
                      <FaUsers />
                    </span>

                    <div>
                      <strong>Workforce management</strong>
                      <p>
                        Bring staff schedules and hospital workforce operations
                        together.
                      </p>
                    </div>
                  </div>

                  <div>
                    <span>
                      <FaRupeeSign />
                    </span>

                    <div>
                      <strong>Connected finance</strong>
                      <p>
                        Keep charges, billing and operational information
                        connected to the patient journey.
                      </p>
                    </div>
                  </div>

                </div>

                <button
                  className="secondary-button hospital-button"
                  onClick={() => scrollTo("contact")}
                >
                  Talk to Us
                  <FaArrowRight />
                </button>

              </div>

            </div>

          </div>
        </section>

        {/* =========================================================
            IPD STORY
        ========================================================= */}

        <section className="journey-section section-space">

          <div className="page-container">

            <div className="section-heading centered">

              <div className="section-tag">
                <FaBed />
                CONNECTED IPD JOURNEY
              </div>

              <h2>
                From admission to discharge,
                <span> keep the journey connected.</span>
              </h2>

              <p>
                A visual view of how hospital teams can follow the inpatient
                journey across clinical and financial workflows.
              </p>

            </div>

            <div className="journey-flow">

              {ipdSteps.map((step, index) => (
                <React.Fragment key={step.label}>

                  <div className="journey-step">

                    <span>{step.icon}</span>

                    <small>
                      {String(index + 1).padStart(2, "0")}
                    </small>

                    <strong>{step.label}</strong>

                  </div>

                  {index !== ipdSteps.length - 1 && (
                    <div className="journey-line">
                      <FaArrowRight />
                    </div>
                  )}

                </React.Fragment>
              ))}

            </div>

          </div>
        </section>

        {/* =========================================================
            ABDM
        ========================================================= */}

        <section className="abdm-section section-space" id="abdm">

          <div className="page-container abdm-layout">

            <div className="abdm-copy">

              <div className="section-tag abdm-tag">
                <MdHealthAndSafety />
                DIGITAL HEALTH
              </div>

              <h2>
                Built for a more
                <span> connected healthcare future.</span>
              </h2>

              <p>
                The Health App supports connected digital-health workflows,
                helping hospitals move toward interoperable patient journeys
                alongside their everyday hospital operations.
              </p>

              <div className="abdm-points">

                <div>
                  <FaCheckCircle />
                  Connected patient journeys
                </div>

                <div>
                  <FaCheckCircle />
                  Digital healthcare workflows
                </div>

                <div>
                  <FaCheckCircle />
                  Hospital and patient connectivity
                </div>

              </div>

            </div>

            <div className="abdm-visual">

              <div className="connection-center">
                <img src={logo} alt="The Health App" />
                <strong>Health App</strong>
                <small>Connected Care</small>
              </div>

              <div className="connection-node node-patient">
                <span>
                  <FaMobileAlt />
                </span>
                <strong>Patient</strong>
              </div>

              <div className="connection-node node-hospital">
                <span>
                  <FaHospital />
                </span>
                <strong>Hospital</strong>
              </div>

              <div className="connection-node node-doctor">
                <span>
                  <FaUserMd />
                </span>
                <strong>Doctor</strong>
              </div>

              <div className="connection-node node-record">
                <span>
                  <FaFileMedical />
                </span>
                <strong>Health Data</strong>
              </div>

              <div className="connection-ring ring-one" />
              <div className="connection-ring ring-two" />

            </div>

          </div>
        </section>

        {/* =========================================================
            EMERGENCY / CONNECTED CARE
        ========================================================= */}

        <section className="connected-care-section">

          <div className="page-container">

            <div className="connected-care-card">

              <div className="care-icon">
                <MdOutlineEmergency />
              </div>

              <div className="care-copy">
                <small>CONNECTED CARE</small>
                <h2>Healthcare should feel connected, not complicated.</h2>
                <p>
                  One ecosystem connecting patient experiences with hospital
                  operations.
                </p>
              </div>

              <a
                href="https://play.google.com/store/apps/details?id=com.health.health_us&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                className="light-button"
              >
                Get Started
                <FaArrowRight />
              </a>

            </div>

          </div>
        </section>

        {/* =========================================================
            CONTACT
        ========================================================= */}

        <section className="contact-section section-space" id="contact">

          <div className="page-container contact-layout">

            <div className="contact-copy">

              <div className="section-tag">
                <FaHeartbeat />
                LET'S CONNECT
              </div>

              <h2>
                Bring The Health App
                <span> to your healthcare journey.</span>
              </h2>

              <p>
                Whether you are looking to book a consultation or want to
                explore The Health App for your hospital, our team is here to
                help.
              </p>

            </div>

            <div className="contact-options">

              <a
                href="https://wa.me/919006146183"
                target="_blank"
                rel="noreferrer"
                className="contact-option"
              >
                <span className="whatsapp">
                  <FaWhatsapp />
                </span>

                <div>
                  <small>WHATSAPP</small>
                  <strong>+91 9006146183</strong>
                </div>

                <FaArrowRight />
              </a>

              <a href="tel:+919006146183" className="contact-option">

                <span className="phone">
                  <FaPhoneAlt />
                </span>

                <div>
                  <small>CALL US</small>
                  <strong>+91 9006146183</strong>
                </div>

                <FaArrowRight />
              </a>

              <a
                href="mailto:info@thehealthapp.in"
                className="contact-option"
              >
                <span className="email">
                  <FaEnvelope />
                </span>

                <div>
                  <small>EMAIL</small>
                  <strong>info@thehealthapp.in</strong>
                </div>

                <FaArrowRight />
              </a>

            </div>

          </div>
        </section>

      </main>

      {/* =========================================================
          FOOTER
      ========================================================= */}

      <footer className="modern-footer">

        <div className="page-container">

          <div className="footer-main">

            <div className="footer-brand-column">

              <div className="footer-brand">
                <img src={logo} alt="The Health App" />
                <span>The Health App</span>
              </div>

              <p>
                Connecting patients, doctors and hospital operations through
                one healthcare ecosystem.
              </p>

            </div>

            <div className="footer-column">
              <strong>Platform</strong>
              <button onClick={() => scrollTo("platform")}>Features</button>
              <button onClick={() => scrollTo("patients")}>For Patients</button>
              <button onClick={() => scrollTo("hospitals")}>For Hospitals</button>
              <button onClick={() => scrollTo("abdm")}>Digital Health</button>
            </div>

            <div className="footer-column">
              <strong>Patients</strong>
<a
  href="https://play.google.com/store/apps/details?id=com.health.health_us&pcampaignid=web_share"
  target="_blank"
  rel="noopener noreferrer"
>
  Book Appointment
</a>
              <button onClick={() => scrollTo("patients")}>
                Appointment Journey
              </button>
              <button onClick={() => scrollTo("contact")}>Support</button>
            </div>

            <div className="footer-column">
              <strong>Legal</strong>
              <Link to="/privacypolicy">Privacy Policy</Link>
              <Link to="/terms&conditions">Terms & Conditions</Link>
              <Link to="/cancellation&refund">
                Cancellation & Refund
              </Link>
            </div>

          </div>

          <div className="footer-bottom">

            <p>
              © {new Date().getFullYear()} The Health App. All Rights Reserved.
            </p>

            <a
              href="https://www.linkedin.com/in/health-app-2a911319b"
              target="_blank"
              rel="noreferrer"
              aria-label="The Health App on LinkedIn"
            >
              <FaLinkedinIn />
            </a>

          </div>

        </div>

      </footer>

    </div>
  );
}

export default Home;